import type { CheckoutCartItem, CheckoutRequest, RatedProductWire } from 'shared';
import { parseDownPayment } from '@/lib/finance.js';
import type { CartEntry, Customer, Vehicle } from '@/lib/widget-types.js';

export interface BuildCheckoutInput {
  quoteId: string;
  customer: Customer;
  vehicle: Vehicle;
  cart: CartEntry[];
  ratedProducts: RatedProductWire[];
}

export type BuildCheckoutFailure = 'stale_cart' | 'empty_cart' | 'missing_financing';

export type BuildCheckoutResult =
  | { ok: true; payload: CheckoutRequest }
  | { ok: false; reason: BuildCheckoutFailure };

export function buildCheckoutPayload(input: BuildCheckoutInput): BuildCheckoutResult {
  const cartItems: CheckoutCartItem[] = [];
  for (const entry of input.cart) {
    // Confirm the item still exists in the current rating before sending it.
    const tier = input.ratedProducts.find((t) => t.quote_item_id === entry.quoteItemId);
    if (tier === undefined) return { ok: false, reason: 'stale_cart' };

    const surcharges = entry.surcharges.map((s) => ({ code: s.code, selected: s.selected }));

    if (entry.payment.type === 'pay_in_full') {
      cartItems.push({
        payment_type: 'pay_in_full',
        quote_item_id: tier.quote_item_id,
        surcharges,
      });
      continue;
    }

    // The bounds themselves are the server's to enforce; this only rejects an amount we
    // couldn't turn into a number at all.
    const downPaymentAmount = parseDownPayment(entry.payment.downPaymentInput);
    if (downPaymentAmount === null) return { ok: false, reason: 'missing_financing' };

    cartItems.push({
      payment_type: 'finance',
      quote_item_id: tier.quote_item_id,
      surcharges,
      financing: {
        duration_months: entry.payment.durationMonths,
        down_payment_amount: downPaymentAmount,
      },
    });
  }
  if (cartItems.length === 0) return { ok: false, reason: 'empty_cart' };

  const successUrl = `${window.location.origin}${window.location.pathname}#/success`;

  const wireCustomer = {
    first_name: input.customer.firstName,
    last_name: input.customer.lastName,
    email: input.customer.email,
    phone: input.customer.phone,
    address: {
      street1: input.customer.address.street1,
      city: input.customer.address.city,
      state: input.customer.address.state,
      postal_code: input.customer.address.postalCode,
      country: input.customer.address.country,
    },
  };
  // Placeholders satisfy the wire schema when decode came back empty — backend
  // only uses mileage on persist.
  const wireVehicle = {
    vin: input.vehicle.vin,
    year: input.vehicle.year ?? 1900,
    make: input.vehicle.make ?? 'Unknown',
    model: input.vehicle.model ?? 'Unknown',
    mileage: input.vehicle.mileage,
  };

  return {
    ok: true,
    payload: {
      quote_id: input.quoteId,
      customer: wireCustomer,
      vehicle: wireVehicle,
      cart: cartItems,
      success_url: successUrl,
    },
  };
}
